import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../components/Iconify';
import { importBundleProductsFromExcel } from '../../utils/importBundleProducts';
import {
  BundleBulkImportResponse,
  BundleBulkImportType,
} from '../../utils/bundleProductBulkImport';

type Props = {
  open: boolean;
  onClose: () => void;
  accessToken: string;
  onImported: () => void;
};

const BundleProductBulkImportModal = ({
  open,
  onClose,
  accessToken,
  onImported,
}: Props) => {
  const excelInputRef = useRef<HTMLInputElement>(null);
  const imagesInputRef = useRef<HTMLInputElement>(null);
  const [importType, setImportType] = useState<BundleBulkImportType>('bundle');
  const [importActiveState, setImportActiveState] = useState(false);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<BundleBulkImportResponse | null>(
    null
  );

  const resetInputs = () => {
    setImportType('bundle');
    setImportActiveState(false);
    setExcelFile(null);
    setImageFiles([]);
    setImportResult(null);
    if (excelInputRef.current) {
      excelInputRef.current.value = '';
    }
    if (imagesInputRef.current) {
      imagesInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (isImporting) return;
    resetInputs();
    onClose();
  };

  const handleImport = async () => {
    if (!excelFile) {
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    const result = await importBundleProductsFromExcel(
      excelFile,
      imageFiles,
      accessToken,
      importType,
      importActiveState
    );

    setIsImporting(false);

    if (!result) {
      return;
    }

    setImportResult(result);

    if (result.created > 0) {
      onImported();
    }
  };

  const isImportComplete = importResult !== null;

  const handleDone = () => {
    resetInputs();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='md'
    >
      <DialogTitle>Bulk Import Products</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <FormControl>
            <Typography
              variant='subtitle2'
              sx={{ mb: 1 }}
            >
              Import type
            </Typography>
            <RadioGroup
              row
              value={importType}
              onChange={(event) => {
                setImportType(event.target.value as BundleBulkImportType);
                setImportResult(null);
              }}
            >
              <FormControlLabel
                value='bundle'
                control={<Radio />}
                label='Bundle'
              />
              <FormControlLabel
                value='flower'
                control={<Radio />}
                label='Flower'
              />
            </RadioGroup>
            <Typography
              variant='caption'
              color='text.secondary'
            >
              {importType === 'bundle'
                ? 'Excel category_name maps to bundle category. Products are created with price 0.'
                : 'Excel category_name maps to flower subcategory under the Flower category. unit_price is imported as product price.'}
            </Typography>
          </FormControl>

          <FormControl>
            <Typography
              variant='subtitle2'
              sx={{ mb: 1 }}
            >
              Import status
            </Typography>
            <RadioGroup
              row
              value={importActiveState ? 'active' : 'inactive'}
              onChange={(event) => {
                setImportActiveState(event.target.value === 'active');
                setImportResult(null);
              }}
            >
              <FormControlLabel
                value='inactive'
                control={<Radio />}
                label='Inactive'
              />
              <FormControlLabel
                value='active'
                control={<Radio />}
                label='Active'
              />
            </RadioGroup>
            <Typography
              variant='caption'
              color='text.secondary'
            >
              Choose whether imported products are active immediately in the store.
            </Typography>
          </FormControl>

          <Box>
            <Typography
              variant='subtitle2'
              sx={{ mb: 1 }}
            >
              1. Excel file
            </Typography>
            <Button
              variant='outlined'
              component='label'
              startIcon={<Iconify icon='eva:file-text-fill' />}
            >
              {excelFile ? excelFile.name : 'Select Excel (.xlsx)'}
              <input
                ref={excelInputRef}
                hidden
                type='file'
                accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  setExcelFile(file);
                  setImportResult(null);
                }}
              />
            </Button>
          </Box>

          <Box>
            <Typography
              variant='subtitle2'
              sx={{ mb: 1 }}
            >
              2. Product images
            </Typography>
            <Button
              variant='outlined'
              component='label'
              startIcon={<Iconify icon='eva:image-fill' />}
            >
              {imageFiles.length
                ? `${imageFiles.length} image(s) selected`
                : 'Select images'}
              <input
                ref={imagesInputRef}
                hidden
                type='file'
                multiple
                accept='image/*'
                onChange={(event) => {
                  const files = event.target.files
                    ? Array.from(event.target.files)
                    : [];
                  setImageFiles(files);
                  setImportResult(null);
                }}
              />
            </Button>
          </Box>

          {importResult && (
            <Box>
              <Typography
                variant='subtitle2'
                sx={{ mb: 1 }}
              >
                Import summary
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mb: 1 }}
              >
                {`Created: ${importResult.created} | Skipped: ${importResult.skipped} | Failed: ${importResult.failed}`}
              </Typography>
              {importResult.flowerCategoryNote && (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 1 }}
                >
                  {importResult.flowerCategoryNote}
                </Typography>
              )}
              <Box
                sx={{
                  maxHeight: 280,
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <List
                  dense
                  disablePadding
                  sx={{ py: 0.5 }}
                >
                  {importResult.results.map((item, index) => (
                    <ListItem
                      key={`${item.rowNumber}-${item.labelEn}`}
                      divider={index < importResult.results.length - 1}
                      sx={{
                        px: 2,
                        py: 1,
                        alignItems: 'flex-start',
                      }}
                    >
                      <ListItemText
                        primary={`Row ${item.rowNumber}: ${item.labelEn}`}
                        secondary={`${item.status.toUpperCase()} - ${item.message}`}
                        primaryTypographyProps={{
                          sx: { wordBreak: 'break-word' },
                        }}
                        secondaryTypographyProps={{
                          sx: { wordBreak: 'break-word' },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {!isImportComplete && (
          <Button
            onClick={handleClose}
            disabled={isImporting}
          >
            Close
          </Button>
        )}
        {isImportComplete ? (
          <Button
            variant='contained'
            onClick={handleDone}
          >
            Done
          </Button>
        ) : (
          <LoadingButton
            variant='contained'
            loading={isImporting}
            disabled={!excelFile}
            onClick={handleImport}
          >
            {isImporting ? 'Importing...' : 'Import'}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BundleProductBulkImportModal;
